import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { isAdmin, logout } from "../utils/auth";
import { XMarkIcon } from "@heroicons/react/24/solid";
import bluepeekLogo from "../assets/images/logo.png";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [databaseDropdownOpen, setDatabaseDropdownOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  const dropdownRef = useRef(null);
  let lastScrollY = useRef(0);

  const navItems = [
    { href: "#about", text: "About Us" },
    { href: "#services", text: "Services" },
    { href: "#database", text: "Database", isMegaDropdown: false },
    { href: "#contact", text: "Reach Us" },
  ];

  const industryLists = [
    { name: "Agriculture", href: "#" },
    { name: "Mining", href: "#" },
    { name: "Construction", href: "#" },
    { name: "Manufacturing", href: "#" },
    { name: "Transportation", href: "#" },
    { name: "Wholesale Trade", href: "#" },
    { name: "Retail", href: "#" },
  ];
  const businessLists = [
    { name: "Financial Services", href: "#" },
    { name: "Business Services", href: "#" },
    { name: "Public Administration", href: "#" },
    { name: "Small Business Owners", href: "#" },
    { name: "Engineering Executives", href: "#" },
    { name: "International Mailing", href: "#" },
    { name: "Government Executives", href: "#" },
  ];

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Handle scroll hide/show navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current) {
        setShowNavbar(false); // scrolling down -> hide
      } else {
        setShowNavbar(true); // scrolling up -> show
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent background scroll on mobile open
  useEffect(() => {
    if (mobileOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        document.body.style.overflow = "";
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileOpen]);

  // Close mega dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        megaOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setMegaOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [megaOpen]);

  // Hamburger icon
  const Hamburger = ({ open, onClick }) => (
    <button
      className="md:hidden flex flex-col justify-center items-center w-9 h-9 focus:outline-none z-50 relative"
      onClick={onClick}
      aria-label="Open menu"
    >
      <span
        className={`block h-0.5 w-6 rounded bg-gray-800 transition-all ${
          open ? "rotate-45 translate-y-2" : ""
        }`}
      />
      <span
        className={`block h-0.5 w-6 rounded bg-gray-800 my-1 transition-all ${
          open ? "opacity-0" : ""
        }`}
      />
      <span
        className={`block h-0.5 w-6 rounded bg-gray-800 transition-all ${
          open ? "-rotate-45 -translate-y-2" : ""
        }`}
      />
    </button>
  );

  return (
    <>
      <nav
        id="main-navbar"
        className={`fixed top-0 left-0 w-full z-50 transition-transform duration-300 ${
          showNavbar ? "translate-y-0" : "-translate-y-full"
        } bg-white border-b border-gray-200`}
      >
        <div className="flex justify-center items-center">
          <div className="h-[70px] w-full max-w-[1400px] relative flex items-center justify-between px-4 md:px-8">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src={bluepeekLogo}
                  alt="BluePeek Logo"
                  className="h-10 w-10 rounded-lg object-cover shadow-sm border border-gray-200 bg-white"
                />
              </Link>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) =>
                item.isMegaDropdown ? (
                  <div key={item.href} className="relative" ref={dropdownRef}>
                    <button
                      className="text-gray-800 text-[18px] font-medium px-3 py-2"
                      onClick={() => {
                        if (isMobile) setMegaOpen((open) => !open);
                      }}
                    >
                      {item.text}
                    </button>
                    {megaOpen && (
                      <div
                        id="mega-dropdown-menu"
                        className="absolute left-1/2 -translate-x-1/2 mt-4 w-[500px] bg-white rounded-lg shadow-xl border border-gray-100 py-6 px-8 flex flex-col md:flex-row gap-8 z-[9999]"
                      >
                        <div className="flex-1 min-w-[200px]">
                          <h4 className="text-base font-semibold mb-3 border-b border-gray-100 pb-2 text-gray-800">
                            Industry Lists
                          </h4>
                          <ul className="space-y-2">
                            {industryLists.map((list) => (
                              <li key={list.name}>
                                <a
                                  href={list.href}
                                  className="block text-gray-800 text-sm"
                                >
                                  {list.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <h4 className="text-base font-semibold mb-3 border-b border-gray-100 pb-2 text-gray-800">
                            Business
                          </h4>
                          <ul className="space-y-2">
                            {businessLists.map((list) => (
                              <li key={list.name}>
                                <a
                                  href={list.href}
                                  className="block text-gray-800 text-sm"
                                >
                                  {list.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-gray-800 text-[18px] font-medium"
                  >
                    {item.text}
                  </a>
                )
              )}
              {isAdmin() && (
                <li>
                  <Link to="/admin/contacts" className="text-gray-800 font-medium">
                    Admin Contacts
                  </Link>
                </li>
              )}
              {isAdmin() && (
                <li>
                  <button
                    onClick={logout}
                    className="text-gray-800 font-medium ml-4"
                  >
                    Logout
                  </button>
                </li>
              )}
            </div>

            {/* Hamburger */}
            <div className="flex md:hidden items-center">
              <Hamburger
                open={mobileOpen}
                onClick={() => setMobileOpen(!mobileOpen)}
              />
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileOpen && (
            <div
              className="fixed inset-0 z-[999] flex items-center justify-end md:hidden"
              style={{
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(8px)",
              }}
              onClick={() => setMobileOpen(false)}
            >
              <div
                className="relative h-full w-4/5 max-w-xs bg-white shadow-xl flex flex-col items-center pt-8 px-6 overflow-y-auto"
                style={{
                  borderTopLeftRadius: "1rem",
                  borderBottomLeftRadius: "1rem",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Btn */}
                <button
                  className="absolute top-4 right-4 p-2 rounded-lg bg-gray-100 text-gray-800"
                  onClick={() => setMobileOpen(false)}
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>

                {/* Logo */}
                <div className="w-full flex items-center justify-center mb-10 mt-2">
                  <div className="flex items-center gap-3">
                    <img
                      src={bluepeekLogo}
                      alt="BluePeek Logo"
                      className="h-9 w-9 rounded-md object-cover border border-gray-200"
                    />
                  </div>
                </div>

                {/* Nav Items */}
                <ul className="flex flex-col gap-6 text-center w-full mt-4">
                  {navItems.map((item) =>
                    item.isMegaDropdown ? (
                      <li key={item.href} className="relative">
                        <button
                          className="w-full flex items-center justify-between text-gray-800 text-lg font-medium py-2"
                          onClick={() =>
                            setDatabaseDropdownOpen((open) => !open)
                          }
                        >
                          <span>{item.text}</span>
                          <svg
                            className={`w-5 h-5 ml-2 ${
                              databaseDropdownOpen ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        {databaseDropdownOpen && (
                          <div className="w-full mt-4 bg-gray-50 rounded-md shadow-sm border border-gray-200 py-4 px-4 flex flex-col gap-4">
                            <div>
                              <h4 className="text-base font-semibold text-gray-800 mb-2">
                                Industry Lists
                              </h4>
                              <ul className="space-y-2">
                                {industryLists.map((item) => (
                                  <li key={item.name}>
                                    <a
                                      href={item.href}
                                      className="text-gray-800 text-sm block py-1"
                                    >
                                      {item.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="text-base font-semibold text-gray-800 mb-2">
                                Business
                              </h4>
                              <ul className="space-y-2">
                                {businessLists.map((item) => (
                                  <li key={item.name}>
                                    <a
                                      href={item.href}
                                      className="text-gray-800 text-sm block py-1"
                                    >
                                      {item.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </li>
                    ) : (
                      <li key={item.href}>
                        <a
                          href={item.href}
                          className="text-gray-800 text-lg font-medium block py-2"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.text}
                        </a>
                      </li>
                    )
                  )}
                </ul>

                {/* Admin */}
                {isAdmin() && (
                  <div className="w-full border-t border-gray-200 my-6"></div>
                )}
                {isAdmin() && (
                  <ul className="flex flex-col gap-4 w-full">
                    <li>
                      <Link
                        to="/admin/contacts"
                        className="text-gray-800 text-lg font-medium block py-2"
                        onClick={() => setMobileOpen(false)}
                      >
                        Admin Contacts
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          logout();
                          setMobileOpen(false);
                        }}
                        className="text-gray-800 text-lg font-medium block py-2"
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
